import type { FC, ReactNode } from 'react'

import { cn } from '@/lib/utils'
import { delay } from '@/lib/motion'

/*
 * Small, filled scenes of the old way of hiring, shown on the "Traditional recruiting" and
 * "What we never do" cards. Each is drawn at a fixed 360 × 300 with real text and scaled to its
 * card by MockScale. Their rows play in once when the card enters.
 */

export const SCENE_WIDTH = 360

const SURFACE = 'rounded-[8px] border-[0.5px] border-ink/15 bg-white shadow-[0_1px_2px_rgb(10_13_18/0.05)]'

interface SceneProps {
    children: ReactNode
    className?: string
}

const Scene: FC<SceneProps> = ({ children, className }): ReactNode => {
    return (
        <div aria-hidden='true' className={cn('reveal-group relative h-[300px] w-full overflow-hidden rounded-[8px] bg-[#f3f0ea] p-3', className)}>
            {children}
        </div>
    )
}

const AVATAR_TONES = ['bg-cloud text-ink', 'bg-clay/15 text-clay-deep', 'bg-pane text-ink/80']

const Initials: FC<{ name: string, index: number, className?: string }> = ({ name, index, className }): ReactNode => {
    const letters = name.split(' ').map(part => part[0]).slice(0, 2).join('')

    return (
        <span className={cn('flex size-6 shrink-0 items-center justify-center rounded-full text-[9.5px] font-semibold', AVATAR_TONES[index % AVATAR_TONES.length], className)}>
            {letters}
        </span>
    )
}

const Fade: FC = (): ReactNode => {
    return <span className='pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-[linear-gradient(180deg,rgb(243_240_234/0),#f3f0ea_85%)]' />
}

/* Inbound applications overwhelm: an applicant list that never ends ---------------------- */

const APPLICANTS = [
    { name: 'Jordan Blake', meta: 'Applied 2h ago · Lisbon' },
    { name: 'Amara Okafor', meta: 'Applied 3h ago · Lagos' },
    { name: 'Lukas Meyer', meta: 'Applied 3h ago · Berlin' },
    { name: 'Sara Lindgren', meta: 'Applied 5h ago · Malmö' },
    { name: 'Diego Ramos', meta: 'Applied 6h ago · Remote' },
    { name: 'Mina Cho', meta: 'Applied 8h ago · Seoul' }
]

export const ApplicationsScene: FC = (): ReactNode => {
    return (
        <Scene className='flex flex-col gap-2'>
            <div className={cn(SURFACE, 'rise flex items-center justify-between px-3 py-2.5')} style={delay(80)}>
                <div>
                    <p className='text-[13px] font-semibold leading-tight text-ink'>Senior Product Designer</p>
                    <p className='mt-0.5 text-[10.5px] text-ink/55'>Applications · Careers page</p>
                </div>
                <span className='pop rounded-full bg-clay px-2 py-0.5 text-[11px] font-semibold text-paper' style={delay(500)}>
                    312 new
                </span>
            </div>

            <ul className='flex flex-col gap-1.5'>
                {APPLICANTS.map((applicant, index) => (
                    <li key={applicant.name} className={cn(SURFACE, 'row-in flex items-center gap-2.5 px-2.5 py-[7px]')} style={delay(250 + index * 110)}>
                        <Initials name={applicant.name} index={index} />
                        <span className='min-w-0 flex-1'>
                            <span className='block truncate text-[12px] font-medium leading-tight text-ink'>{applicant.name}</span>
                            <span className='block text-[10px] leading-tight text-ink/50'>{applicant.meta}</span>
                        </span>
                        <span className='rounded-full border border-azure/30 bg-cloud/50 px-1.5 py-px text-[9.5px] font-medium text-ink/70'>
                            Unreviewed
                        </span>
                    </li>
                ))}
            </ul>

            <Fade />
            <p className='hand absolute bottom-3 right-4 z-10 text-[19px] leading-none text-ink/70 [rotate:-6deg]'>+306 more</p>
        </Scene>
    )
}

/* Hiring turns into a second job: a week with no room left to design ------------------- */

interface Block {
    day: number
    start: number
    length: number
    label: string
    tone: 'screen' | 'review' | 'chase' | 'work'
}

const BLOCKS: Block[] = [
    { day: 0, start: 9, length: 1, label: 'Screening call', tone: 'screen' },
    { day: 0, start: 10.5, length: 1.5, label: 'Portfolio reviews', tone: 'review' },
    { day: 0, start: 12.5, length: 1, label: 'Chase replies', tone: 'chase' },
    { day: 1, start: 9, length: 1.5, label: 'Screening calls', tone: 'screen' },
    { day: 1, start: 11, length: 1, label: 'Recruiter sync', tone: 'chase' },
    { day: 1, start: 12.5, length: 1.5, label: 'Take-home review', tone: 'review' },
    { day: 2, start: 9.5, length: 1, label: 'Intro call', tone: 'screen' },
    { day: 2, start: 11, length: 2, label: 'Portfolio reviews', tone: 'review' },
    { day: 3, start: 9, length: 1, label: 'Screening call', tone: 'screen' },
    { day: 3, start: 10.5, length: 1, label: 'Write DMs', tone: 'chase' },
    { day: 3, start: 12, length: 2, label: 'Panel interview', tone: 'screen' },
    { day: 4, start: 9, length: 1.5, label: 'Debrief', tone: 'chase' },
    { day: 4, start: 11, length: 1, label: 'Screening call', tone: 'screen' },
    { day: 4, start: 13.25, length: 0.5, label: 'Design work', tone: 'work' }
]

const BLOCK_TONES: Record<Block['tone'], string> = {
    screen: 'border-azure/40 bg-cloud/70 text-ink',
    review: 'border-clay/30 bg-clay/12 text-ink',
    chase: 'border-ink/12 bg-pane text-ink/80',
    work: 'border-dashed border-ink/40 bg-white text-ink/50 line-through'
}

const HOUR = 36
const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri']

export const CalendarScene: FC = (): ReactNode => {
    return (
        <Scene>
            <div className='rise mb-2 flex items-center justify-between' style={delay(80)}>
                <p className='text-[13px] font-semibold text-ink'>This week</p>
                <span className='pop rounded-full bg-ink px-2 py-0.5 text-[10.5px] font-medium text-paper' style={delay(500)}>
                    14 hiring meetings
                </span>
            </div>

            <div className={cn(SURFACE, 'flex overflow-hidden')}>
                <div className='w-7 shrink-0 border-r border-ink/10 pt-5 text-right text-[8.5px] text-ink/40'>
                    {[9, 10, 11, 12, 13].map(hour => (
                        <div key={hour} className='pr-1' style={{ height: HOUR }}>{hour}</div>
                    ))}
                </div>

                {DAYS.map((day, dayIndex) => (
                    <div key={day} className='relative flex-1 border-r border-ink/8 last:border-r-0'>
                        <p className='h-5 border-b border-ink/10 text-center text-[9.5px] font-semibold leading-5 text-ink/60'>{day}</p>
                        <div className='relative' style={{ height: HOUR * 5 }}>
                            {BLOCKS.filter(block => block.day === dayIndex).map((block, index) => (
                                <span
                                    key={`${block.label}-${block.start}`}
                                    className={cn('pop absolute inset-x-[3px] overflow-hidden rounded-[4px] border px-1 py-0.5 text-[8.5px] font-medium leading-[1.15]', BLOCK_TONES[block.tone])}
                                    style={{ top: (block.start - 9) * HOUR + 1, height: block.length * HOUR - 3, ...delay(300 + dayIndex * 120 + index * 70) }}
                                >
                                    {block.label}
                                </span>
                            ))}
                        </div>
                    </div>
                ))}
            </div>

            <p className='hand absolute bottom-3 left-4 text-[19px] leading-none text-ink/70 [rotate:-4deg]'>when do I design?</p>
        </Scene>
    )
}

/* The bar keeps rising: a job spec that wants everything -------------------------------- */

const REQUIREMENTS = [
    { text: '6+ years in product design', stretch: false },
    { text: 'Expert in Figma and prototyping', stretch: false },
    { text: 'Ships production React', stretch: false },
    { text: 'Motion, 3D and illustration', stretch: true },
    { text: 'Runs research and writes strategy', stretch: true },
    { text: 'Fluent in every AI tool', stretch: true },
    { text: 'Can start yesterday', stretch: true }
]

export const SpecScene: FC = (): ReactNode => {
    return (
        <Scene>
            <div className={cn(SURFACE, 'rise h-full px-4 py-3.5')} style={delay(80)}>
                <p className='text-[10px] font-semibold uppercase tracking-[0.08em] text-ink/45'>Job description</p>
                <p className='mt-1 font-serif text-[19px] leading-tight text-ink'>Senior Product Designer</p>
                <p className='mt-2 text-[11px] font-medium text-ink/60'>You will need to be:</p>

                <ul className='mt-1.5 flex flex-col gap-[5px]'>
                    {REQUIREMENTS.map((requirement, index) => (
                        <li key={requirement.text} className='row-in flex items-center gap-2 text-[11.5px] leading-tight' style={delay(300 + index * 140)}>
                            <span className={cn('flex size-3.5 shrink-0 items-center justify-center rounded-[3px] border', requirement.stretch ? 'border-clay/60 bg-clay/10' : 'border-ink/25')}>
                                <span className={cn('size-1.5 rounded-[1px]', requirement.stretch ? 'bg-clay' : 'bg-ink/50')} />
                            </span>
                            <span className={requirement.stretch ? 'font-medium text-clay-deep' : 'text-ink/75'}>{requirement.text}</span>
                        </li>
                    ))}
                </ul>
            </div>

            <span className='pop absolute bottom-6 right-5 rounded-full bg-ink px-2.5 py-1 text-[10.5px] font-medium text-paper' style={delay(1400)}>
                Applicants who match: 0.4%
            </span>
            <p className='hand absolute right-6 top-[46%] text-[22px] leading-none text-clay [rotate:-10deg]'>unicorn?</p>
        </Scene>
    )
}

/* Mass-message designers: the same cold message, over and over ------------------------- */

const COLD = [
    { from: 'Talent Partners', time: '9:12' },
    { from: 'HireFast Recruiting', time: '9:04' },
    { from: 'StaffWise', time: '8:51' },
    { from: 'Nimbly Careers', time: '8:37' },
    { from: 'TopTalent Group', time: '8:20' },
    { from: 'Brightway Search', time: '8:02' }
]

export const ColdInboxScene: FC = (): ReactNode => {
    return (
        <Scene className='flex flex-col gap-2'>
            <div className='rise flex items-center justify-between px-0.5' style={delay(80)}>
                <p className='text-[13px] font-semibold text-ink'>Inbox</p>
                <span className='pop rounded-full bg-azure px-2 py-0.5 text-[10.5px] font-semibold text-white' style={delay(500)}>
                    486 unread
                </span>
            </div>

            <ul className={cn(SURFACE, 'flex flex-col divide-y divide-ink/8 overflow-hidden')}>
                {COLD.map((message, index) => (
                    <li key={message.from} className='row-in flex items-start gap-2 px-2.5 py-2' style={delay(250 + index * 110)}>
                        <span className='mt-1.5 size-1.5 shrink-0 rounded-full bg-azure' />
                        <Initials name={message.from} index={index} className='size-5 text-[8.5px]' />
                        <span className='min-w-0 flex-1'>
                            <span className='flex items-baseline justify-between gap-2'>
                                <span className='truncate text-[11px] font-semibold text-ink'>{message.from}</span>
                                <span className='shrink-0 text-[9px] text-ink/45'>{message.time}</span>
                            </span>
                            <span className='block truncate text-[10.5px] text-ink/60'>
                                Hi <span className='rounded-[3px] bg-clay/15 px-0.5 font-medium text-clay-deep'>{'{first_name}'}</span>, I came across your profile and
                            </span>
                        </span>
                    </li>
                ))}
            </ul>

            <Fade />
        </Scene>
    )
}

/* Pass off a résumé dump as a shortlist: a spreadsheet of 214 names --------------------- */

const ROWS = [
    { name: 'Alex Turner', title: 'UI Designer', note: '?' },
    { name: 'Priya Shah', title: 'Product Designer', note: 'maybe' },
    { name: 'Tom Becker', title: 'Graphic Designer', note: 'no portfolio' },
    { name: 'Lena Fischer', title: 'UX Researcher', note: '' },
    { name: 'Omar Haddad', title: 'Visual Designer', note: 'seen before' },
    { name: 'Chloé Martin', title: 'Product Designer', note: '?' },
    { name: 'Ryan Cole', title: 'Web Designer', note: 'junior?' },
    { name: 'Ines Duarte', title: 'Design Lead', note: '' },
    { name: 'Kenji Mori', title: 'UI/UX Designer', note: 'maybe' }
]

export const SpreadsheetScene: FC = (): ReactNode => {
    return (
        <Scene>
            <div className={cn(SURFACE, 'rise overflow-hidden')} style={delay(80)}>
                <div className='flex items-center gap-2 border-b border-ink/10 bg-[#f8f6f5] px-2.5 py-1.5'>
                    <span className='size-2.5 rounded-[2px] bg-[#7f9a6b]' />
                    <p className='text-[10.5px] font-medium text-ink/80'>candidates_final_v7.xlsx</p>
                </div>

                <table className='w-full table-fixed text-left text-[9.5px] leading-tight'>
                    <thead>
                        <tr className='bg-[#f8f6f5] text-ink/50'>
                            <th className='w-6 border-b border-r border-ink/10 px-1 py-1 font-medium'>#</th>
                            <th className='border-b border-r border-ink/10 px-1.5 py-1 font-medium'>Name</th>
                            <th className='border-b border-r border-ink/10 px-1.5 py-1 font-medium'>Title</th>
                            <th className='w-[72px] border-b border-ink/10 px-1.5 py-1 font-medium'>Notes</th>
                        </tr>
                    </thead>
                    <tbody>
                        {ROWS.map((row, index) => (
                            <tr key={row.name} className={cn('row-in', index === 4 && 'bg-clay/10')} style={delay(250 + index * 90)}>
                                <td className='border-b border-r border-ink/8 px-1 py-[5px] text-ink/40'>{index + 1}</td>
                                <td className='truncate border-b border-r border-ink/8 px-1.5 py-[5px] font-medium text-ink'>{row.name}</td>
                                <td className='truncate border-b border-r border-ink/8 px-1.5 py-[5px] text-ink/70'>{row.title}</td>
                                <td className='truncate border-b border-ink/8 px-1.5 py-[5px] text-ink/50'>{row.note}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <Fade />
            <p className='absolute bottom-3.5 left-4 z-10 text-[10.5px] font-medium text-ink/55'>214 rows · sent by an agency</p>
            <p className='hand absolute bottom-3 right-4 z-10 text-[20px] leading-none text-clay [rotate:-6deg]'>a shortlist?</p>
        </Scene>
    )
}

/* Take on roles we can't fill: a post that has been open for months -------------------- */

const DETAILS = [
    ['Salary', '$60k'],
    ['Experience', '12+ years'],
    ['Must know', 'Figma, React, Webflow, 3D, copywriting'],
    ['Team', 'Just you']
]

export const StalledRoleScene: FC = (): ReactNode => {
    return (
        <Scene>
            <div className={cn(SURFACE, 'rise h-full px-4 py-3.5')} style={delay(80)}>
                <div className='flex items-center justify-between gap-2'>
                    <span className='rounded-full border border-clay/40 bg-clay/10 px-2 py-0.5 text-[10px] font-semibold text-clay-deep'>Still open</span>
                    <span className='text-[10px] text-ink/50'>Posted 94 days ago</span>
                </div>

                <p className='mt-2.5 font-serif text-[18px] leading-tight text-ink'>Lead Designer (design, engineering and marketing)</p>

                <dl className='mt-3 flex flex-col gap-1.5 text-[11px] leading-tight'>
                    {DETAILS.map(([label, value], index) => (
                        <div key={label} className='row-in flex gap-2' style={delay(300 + index * 140)}>
                            <dt className='w-[72px] shrink-0 text-ink/45'>{label}</dt>
                            <dd className='font-medium text-ink/80'>{value}</dd>
                        </div>
                    ))}
                </dl>

                <div className='mt-3.5 flex gap-2'>
                    <span className='rounded-[6px] bg-pane px-2 py-1 text-[10.5px] text-ink/70'>
                        Applicants <b className='font-semibold text-ink'>1,204</b>
                    </span>
                    <span className='rounded-[6px] bg-pane px-2 py-1 text-[10.5px] text-ink/70'>
                        Qualified <b className='font-semibold text-clay-deep'>0</b>
                    </span>
                </div>
            </div>

            <p className='hand absolute bottom-1 right-6 text-[64px] leading-none text-clay/80 [rotate:12deg]'>?</p>
        </Scene>
    )
}
